import { Box, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Status from '@components/Status';

import type { AllergyRecord } from '@models/allergy';
import type { ImmunizationRecord } from '@models/immunization';
import type { MedicalConditionRecord } from '@models/medical-condition';
import type { MedicationStatementRecord } from '@models/medication';
import type { ProcedureRecord } from '@models/procedure';

import { buildOccurrenceCounts } from '@utils/health-summary';

import {
  ApprovedDiv,
  InfoSection,
} from '@views/HealthSummary/Directives/DirectivesEntry.style';
import { InfoH2, TitleH2 } from '@views/HealthSummary/HealthSummary.style';

import { Occurrence, OccurrenceName } from './SummarySection.style';

const messages = defineMessages({
  unknown: {
    id: 'GLOBAL.unknown',
    defaultMessage: 'Unknown',
  },
  severe: {
    id: 'summarySection.severe',
    defaultMessage: 'Severe',
  },
  noAllergies: {
    id: 'summarySection.noAllergies',
    defaultMessage: 'No allergy records reported',
  },
});

type SummaryRecord =
  | AllergyRecord
  | ImmunizationRecord
  | MedicalConditionRecord
  | MedicationStatementRecord
  | ProcedureRecord;

function SummarySection({
  // useOption = false,
  // override = null,
  // titleKey = '',
  title = '',
  route = '',
  clickable = true,
  addStatus = false,
  countOccurrences = false,
  data = [],
}: {
  // useOption?: boolean;
  // override?: string | null;
  // titleKey: string;
  title: string;
  route: string;
  clickable?: boolean;
  addStatus?: boolean;
  countOccurrences?: boolean;
  data: SummaryRecord[];
}) {
  const { formatMessage } = useIntl();
  const vaccineNames: string[] = [];
  const immunizationOccurrenceCounts = countOccurrences
    ? buildOccurrenceCounts(data, (record) =>
        record.recordType === 'ImmunizationModel'
          ? record.vaccineName
          : undefined
      )
    : null;

  const { palette } = useTheme();

  const getOccurrenceName = (name: string) => {
    if (vaccineNames.includes(name)) {
      return '';
    }
    vaccineNames.push(name);

    const occurrences = immunizationOccurrenceCounts?.get(name) ?? 0;
    return (
      <OccurrenceName>
        {occurrences > 1 && <Occurrence>{`${occurrences}x`}</Occurrence>}
        {name}
      </OccurrenceName>
    );
  };

  const getFormattedName = (record: SummaryRecord, name: string) => {
    if (addStatus) {
      return record.recordType === 'AllergyModel' && record.isSevere ? (
        <Stack direction="row" spacing={1}>
          <Box>{name}</Box>
          <Status
            greyText
            helperText={formatMessage(messages.severe)}
            helperTextColor={palette.error.main}
            position="right"
            variant="red"
          />
        </Stack>
      ) : (
        name
      );
    }
    if (countOccurrences) {
      return getOccurrenceName(name);
    }
    return name;
  };

  const handleClick = (record: SummaryRecord) => {
    if (clickable && route) {
      navigate(`${route}${record.id}`);
    }
  };

  return (
    <Box my={1}>
      <TitleH2>{title}</TitleH2>
      {data.length === 0 ? (
        <InfoH2 clickable={false} key="nodata">
          {formatMessage(
            route.includes('allergies')
              ? messages.noAllergies
              : messages.unknown
          )}
        </InfoH2>
      ) : (
        <ApprovedDiv>
          {data.map((record) => {
            let name = '';
            if (record.recordType === 'AllergyModel') {
              name = record.substance ?? '';
            } else if (record.recordType === 'ConditionModel') {
              name = record.condition ?? '';
            } else if (record.recordType === 'ProcedureModel') {
              name = record.procedureName;
            } else if (record.recordType === 'MedicationStatementModel') {
              name = record.medication ?? '';
            } else if (record.recordType === 'ImmunizationModel') {
              name = record.vaccineName ?? '';
            }
            // if (useOption) {
            //   if (override) {
            //     const overrideName = record.getString(override);
            //     if (overrideName) {
            //       name = overrideName;
            //     } else {
            //       name = record.getOption(titleKey, survey);
            //     }
            //   }
            // } else {
            //   name = useOption
            //     ? record.getOption(titleKey, survey)
            //     : record.getString(titleKey);
            // }
            return record.recordType === 'ImmunizationModel' ? (
              <InfoH2
                clickable={clickable}
                key={record.id}
                onClick={() => handleClick}
              >
                {getFormattedName(record, name)}
              </InfoH2>
            ) : (
              <InfoSection
                clickable={clickable}
                key={record.id}
                onClick={() => handleClick}
              >
                {getFormattedName(record, name)}
              </InfoSection>
            );
          })}
        </ApprovedDiv>
      )}
    </Box>
  );
}

export default SummarySection;
